import React, { useState, useEffect } from 'react';
import { useParams } from 'react-router-dom';
import { Loader2 } from 'lucide-react';
import Header from '../components/Header';
import Footer from '../components/Footer';
import WhatsAppButton from '../components/WhatsAppButton';
import Breadcrumbs from '../components/Breadcrumbs';
import ProductCarousel from '../components/ProductCarousel';

const API_URL = process.env.REACT_APP_BACKEND_URL;

const ProductsCatalogPage = () => {
  const { country } = useParams();
  const [categories, setCategories] = useState([]);
  const [productsByCategory, setProductsByCategory] = useState({});
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    const fetchData = async () => {
      try {
        // Fetch categories
        const categoriesRes = await fetch(`${API_URL}/api/categories`);
        const categoriesData = await categoriesRes.json();
        setCategories(categoriesData);

        // Fetch products for each category
        const productsMap = {};
        for (const category of categoriesData) {
          const productsRes = await fetch(`${API_URL}/api/products?category_id=${category.id}&limit=20`);
          const productsData = await productsRes.json();
          productsMap[category.id] = productsData;
        }
        setProductsByCategory(productsMap);
      } catch (error) {
        console.error('Error fetching data:', error);
      } finally {
        setLoading(false);
      }
    };
    
    fetchData();
  }, []);

  const breadcrumbItems = [
    { label: country?.toUpperCase() || 'BR', link: `/${country}` },
    { label: 'Produtos', link: `/${country}/products` },
    { label: 'Catálogo', link: null }
  ];

  if (loading) {
    return (
      <div className="min-h-screen bg-aug-cream flex items-center justify-center">
        <Loader2 className="w-12 h-12 animate-spin text-aug-blue" />
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-aug-cream">
      <Header />
      <WhatsAppButton />
      
      <div className="pt-20">
        <Breadcrumbs items={breadcrumbItems} />

        {/* Hero */}
        <section className="bg-gradient-to-r from-aug-blue to-aug-green text-white py-4xl">
          <div className="max-w-[1400px] mx-auto px-xl text-center">
            <h1 className="text-4xl md:text-5xl font-bold mb-4">
              Produtos Inovadores
            </h1>
            <p className="text-xl max-w-3xl mx-auto">
              Explore nosso catálogo completo de produtos físicos e digitais. 
              Qualidade, confiabilidade e suporte técnico em cada produto.
            </p>
          </div>
        </section>

        {/* Categories - Netflix Style */}
        <section className="py-4xl bg-white">
          <div className="max-w-[1400px] mx-auto">
            {categories.map((category) => {
              const categoryProducts = productsByCategory[category.id] || [];
              if (categoryProducts.length === 0) return null;

              return (
                <ProductCarousel
                  key={category.id}
                  title={category.name}
                  products={categoryProducts}
                />
              );
            })}
          </div>
        </section>

        {/* About Products */}
        <section className="py-4xl bg-aug-cream">
          <div className="max-w-[1400px] mx-auto px-xl text-center">
            <h2 className="text-3xl font-bold text-aug-gray-dark mb-4">
              Compromisso com Excelência
            </h2>
            <div className="grid grid-cols-1 md:grid-cols-3 gap-8 mt-8">
              <div>
                <div className="w-16 h-16 bg-aug-blue-light rounded-full flex items-center justify-center mx-auto mb-4">
                  <span className="text-3xl">🎯</span>
                </div>
                <h3 className="text-xl font-semibold text-aug-gray-dark mb-2">Transparência</h3>
                <p className="text-aug-gray-medium">
                  Todas as informações sobre produtos, especificações e preços são claras e transparentes
                </p>
              </div>
              <div>
                <div className="w-16 h-16 bg-aug-green-light rounded-full flex items-center justify-center mx-auto mb-4">
                  <span className="text-3xl">📚</span>
                </div>
                <h3 className="text-xl font-semibold text-aug-gray-dark mb-2">Educação</h3>
                <p className="text-aug-gray-medium">
                  Ajudamos você a escolher o produto certo com descrições detalhadas e especificações técnicas
                </p>
              </div>
              <div>
                <div className="w-16 h-16 bg-aug-salmon rounded-full flex items-center justify-center mx-auto mb-4">
                  <span className="text-3xl">⭐</span>
                </div>
                <h3 className="text-xl font-semibold text-aug-gray-dark mb-2">Excelência</h3>
                <p className="text-aug-gray-medium">
                  Suporte dedicado e produtos de alta qualidade para garantir sua satisfação
                </p>
              </div>
            </div>
          </div>
        </section>
      </div>

      <Footer />
    </div>
  );
};

export default ProductsCatalogPage;
